import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { api, onSessionChange, refreshSession, setAccessToken } from '../api/client.js';

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const permissions = ref([]);
  const initialized = ref(false);
  const loading = ref(false);

  const authenticated = computed(() => Boolean(user.value));
  const isAdmin = computed(() => Boolean(user.value?.isPlatformAdmin));
  const canInvite = computed(() => isAdmin.value || permissions.value.includes('employees.invite'));
  const canManageEmployees = computed(() => isAdmin.value || permissions.value.includes('employees.manage'));
  const canViewEmployeeSensitive = computed(() => isAdmin.value || permissions.value.includes('employees.sensitive'));

  function acceptSession(session) {
    user.value = session?.user || null;
    permissions.value = session?.effectivePermissions || session?.user?.effectivePermissions || [];
    setAccessToken(session?.accessToken || null);
  }

  onSessionChange(acceptSession);

  async function bootstrap() {
    if (initialized.value) return;
    loading.value = true;
    try {
      acceptSession(await refreshSession());
    } catch {
      acceptSession(null);
    } finally {
      initialized.value = true;
      loading.value = false;
    }
  }

  async function login(credentials) {
    loading.value = true;
    try {
      const { data } = await api.post('/auth/login', credentials, { skipAuthRefresh: true });
      acceptSession(data);
      initialized.value = true;
      return data;
    } finally {
      loading.value = false;
    }
  }

  async function completeInvitation(token, form) {
    const { data } = await api.post(`/invitations/${encodeURIComponent(token)}/register`, form, {
      skipAuthRefresh: true
    });
    acceptSession(data);
    initialized.value = true;
    return data;
  }

  async function logout() {
    try {
      await api.post('/auth/logout', null, { skipAuthRefresh: true });
    } finally {
      acceptSession(null);
      initialized.value = true;
    }
  }

  function hasPermission(permission) {
    return isAdmin.value || permissions.value.includes('*') || permissions.value.includes(permission);
  }

  return {
    user,
    permissions,
    initialized,
    loading,
    authenticated,
    isAdmin,
    canInvite,
    canManageEmployees,
    canViewEmployeeSensitive,
    bootstrap,
    login,
    logout,
    completeInvitation,
    hasPermission,
    acceptSession
  };
});
